import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../auth/contexts/AuthenticationContext';
import { classifyPendingActionError } from '../../shared/api/errors';
import { invalidateAll, SELF_ENROLL_INVALIDATION_KEYS } from '../../shared/api/cacheInvalidation';
import { readSelfEnrollSlug, clearSelfEnrollSlug } from '../organization/pendingInviteClaim';

import {
  courseApi,
  enrollmentApi,
  CreateCourseParams,
} from './endpoints';

export type { Course, CreateCourseParams, Enrollment, OrgPackage, EnrollmentsResponse } from './endpoints';

export function useCourses(
  filters?: { division?: string; delivery_type?: string; has_lms?: boolean; include_inactive?: boolean },
  options?: { enabled?: boolean },
) {
  // Normalize empty/undefined to a stable `null` so the query cache key doesn't
  // vary between `undefined` and `{}`.
  const filterKey = filters && Object.keys(filters).length > 0 ? filters : null;
  return useQuery({
    queryKey: ['courses', 'list', filterKey],
    queryFn: () => courseApi.list(filters),
    enabled: options?.enabled ?? true,
  });
}

// LMS-eligible (thinkific + hybrid) courses — anything Enrollment can attach
// to. The backend's has_lms filter mirrors Course#has_lms? so we don't have
// to keep the delivery-type allowlist in sync on the client.
export function useLmsCourses(options?: { enabled?: boolean }) {
  return useCourses({ has_lms: true }, options);
}

export function useCourse(id: number | string | null | undefined) {
  return useQuery({
    queryKey: ['courses', 'detail', id],
    queryFn: () => {
      if (!id) throw new Error('useCourse requires an id');
      return courseApi.get(id);
    },
    enabled: !!id,
  });
}

export function useCreateCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateCourseParams) => courseApi.create(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

export function useUpdateCourse(id: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Partial<CreateCourseParams>) => courseApi.update(id, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    },
  });
}

export function useArchiveCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, moveEventsToCourseId }: { id: number | string; moveEventsToCourseId?: number | null }) =>
      courseApi.archive(id, { moveEventsToCourseId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useEnrollments() {
  return useQuery({
    queryKey: ['enrollments'],
    queryFn: enrollmentApi.list,
  });
}

export function useSyncEnrollments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => enrollmentApi.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useLeaveCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { enrollmentId: number | string }) =>
      enrollmentApi.leave(params.enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
    },
  });
}

// Fires the self-enroll call once after auth is ready if a course slug is queued
// from the /invite landing page. Mounted globally by InviteClaimBanner for the
// same reasons as usePendingInviteLinkClaim.
export function usePendingSelfEnroll(
  onEnrolled?: () => void,
  onError?: (message: string) => void,
) {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();
  const firedRef = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || firedRef.current) return;
    const slug = readSelfEnrollSlug();
    if (!slug) return;

    firedRef.current = true;
    enrollmentApi
      .selfEnroll(slug)
      .then(() => {
        clearSelfEnrollSlug();
        invalidateAll(queryClient, SELF_ENROLL_INVALIDATION_KEYS);
        onEnrolled?.();
      })
      .catch((err: unknown) => {
        const { definitive, message } = classifyPendingActionError(err, {
          definitiveFallback: 'We could not add your course automatically. Please contact your administrator.',
          unreachable: "We couldn't reach the Human Risk Academy to add your course. Check your connection and refresh this page.",
        });
        if (definitive) clearSelfEnrollSlug();
        else firedRef.current = false;
        onError?.(message);
      });
  }, [isAuthenticated, isLoading, queryClient, onEnrolled, onError]);
}

export function useCreateCourseCheckout(courseId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { discount_code?: string }) =>
      courseApi.checkout(courseId, opts),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      window.location.href = data.url;
    },
  });
}
